/**
 * useBookingView Hook
 *
 * Manages view mode (week/month/year) and display-related settings.
 * Provides date formatting and view-specific calculations.
 */

import type { BookingViewMode } from "@/services/types";
import {
  bookingFiltersAtom,
  bookingViewModeAtom,
  hideWeekendAtom,
} from "@/store/bookingStore";
import {
  formatMonthName,
  formatMonthYear,
  formatShortMonthName,
  formatShortWeekday,
  formatWeekday,
} from "@/utils/date";
import {
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getMonth,
  getWeek,
  getYear,
  isSameMonth,
  isToday,
  isWeekend,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { nb } from "date-fns/locale";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";

/**
 * Week info for month/year views
 */
export interface WeekInfo {
  weekNumber: number;
  startDate: Date;
  endDate: Date;
  days: Date[];
  isCurrentWeek: boolean;
}

/**
 * Month info for year view
 */
export interface MonthInfo {
  monthNumber: number;
  name: string;
  shortName: string;
  startDate: Date;
  endDate: Date;
  isCurrentMonth: boolean;
}

/**
 * Day info for rendering
 */
export interface DayInfo {
  date: Date;
  dayNumber: number;
  dayName: string;
  shortDayName: string;
  isToday: boolean;
  isWeekend: boolean;
  isCurrentMonth: boolean;
  weekNumber: number;
}

export function useBookingView() {
  // Core state
  const [viewMode, setViewMode] = useAtom(bookingViewModeAtom);
  const [hideWeekend, setHideWeekend] = useAtom(hideWeekendAtom);
  const filters = useAtomValue(bookingFiltersAtom);

  const { start: dateStart, end: dateEnd } = filters.dateRange;

  // ============ VIEW MODE HELPERS ============

  /**
   * Check if current view is week view
   */
  const isWeekView = viewMode === "week";

  /**
   * Check if current view is month view
   */
  const isMonthView = viewMode === "month";

  /**
   * Check if current view is year view
   */
  const isYearView = viewMode === "year";

  // ============ DATE RANGE INFO ============

  /**
   * All days in current date range
   */
  const allDays = useMemo((): Date[] => {
    return eachDayOfInterval({ start: dateStart, end: dateEnd });
  }, [dateStart, dateEnd]);

  /**
   * Visible days (filtered by hideWeekend)
   */
  const visibleDays = useMemo((): Date[] => {
    if (!hideWeekend) return allDays;
    return allDays.filter((d) => !isWeekend(d));
  }, [allDays, hideWeekend]);

  /**
   * Number of days in range
   */
  const dayCount = useMemo((): number => {
    return differenceInDays(dateEnd, dateStart) + 1;
  }, [dateStart, dateEnd]);

  /**
   * Number of visible days
   */
  const visibleDayCount = useMemo((): number => {
    return visibleDays.length;
  }, [visibleDays]);

  // ============ DETAILED DAY INFO ============

  /**
   * Detailed info for each day
   */
  const dayInfos = useMemo((): DayInfo[] => {
    return visibleDays.map((date) => ({
      date,
      dayNumber: date.getDate(),
      dayName: formatWeekday(date),
      shortDayName: formatShortWeekday(date),
      isToday: isToday(date),
      isWeekend: isWeekend(date),
      isCurrentMonth: isSameMonth(date, new Date()),
      weekNumber: getWeek(date, { weekStartsOn: 1 }),
    }));
  }, [visibleDays]);

  // ============ WEEK INFO (for month view) ============

  /**
   * Get weeks in the current date range
   */
  const weeks = useMemo((): WeekInfo[] => {
    const weekStarts = eachWeekOfInterval(
      { start: dateStart, end: dateEnd },
      { weekStartsOn: 1 },
    );

    return weekStarts.map((weekStart) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
      const currentWeekNumber = getWeek(new Date(), { weekStartsOn: 1 });

      return {
        weekNumber: getWeek(weekStart, { weekStartsOn: 1 }),
        startDate: weekStart,
        endDate: weekEnd,
        days: hideWeekend ? days.filter((d) => !isWeekend(d)) : days,
        isCurrentWeek:
          getWeek(weekStart, { weekStartsOn: 1 }) === currentWeekNumber,
      };
    });
  }, [dateStart, dateEnd, hideWeekend]);

  /**
   * Number of weeks in range
   */
  const weekCount = useMemo((): number => {
    return differenceInWeeks(dateEnd, dateStart) + 1;
  }, [dateStart, dateEnd]);

  // ============ MONTH INFO (for year view) ============

  /**
   * Get months in the current date range
   */
  const months = useMemo((): MonthInfo[] => {
    const monthStarts = eachMonthOfInterval({ start: dateStart, end: dateEnd });

    return monthStarts.map((monthStart) => {
      const monthEnd = endOfMonth(monthStart);
      const currentMonth = getMonth(new Date());

      return {
        monthNumber: getMonth(monthStart) + 1, // 1-indexed
        name: formatMonthName(monthStart),
        shortName: formatShortMonthName(monthStart),
        startDate: monthStart,
        endDate: monthEnd,
        isCurrentMonth: getMonth(monthStart) === currentMonth,
      };
    });
  }, [dateStart, dateEnd]);

  /**
   * Number of months in range
   */
  const monthCount = useMemo((): number => {
    return differenceInMonths(dateEnd, dateStart) + 1;
  }, [dateStart, dateEnd]);

  // ============ FORMATTING ============

  /**
   * Format date range as display string
   */
  const dateRangeLabel = useMemo((): string => {
    switch (viewMode) {
      case "week": {
        const weekNum = getWeek(dateStart, { weekStartsOn: 1 });
        return `Uke ${weekNum}, ${getYear(dateStart)}`;
      }
      case "month":
        return formatMonthYear(dateStart);
      case "year":
        return format(dateStart, "yyyy");
      default:
        return `${format(dateStart, "d. MMM", { locale: nb })} - ${format(
          dateEnd,
          "d. MMM yyyy",
          { locale: nb },
        )}`;
    }
  }, [viewMode, dateStart, dateEnd]);

  /**
   * Short date range label
   */
  const shortDateRangeLabel = useMemo((): string => {
    switch (viewMode) {
      case "week":
        return `Uke ${getWeek(dateStart, { weekStartsOn: 1 })}`;
      case "month":
        return `${formatShortMonthName(dateStart)} ${format(
          dateStart,
          "yyyy",
        )}`;
      case "year":
        return format(dateStart, "yyyy");
      default:
        return `${format(dateStart, "d/M")} - ${format(dateEnd, "d/M")}`;
    }
  }, [viewMode, dateStart, dateEnd]);

  /**
   * Format a date for display
   */
  const formatDate = useCallback((date: Date, formatStr = "PPP"): string => {
    return format(date, formatStr, { locale: nb });
  }, []);

  /**
   * Format day header (for grid view)
   */
  const formatDayHeader = useCallback((date: Date): string => {
    return format(date, "EEE d", { locale: nb });
  }, []);

  /**
   * Format week header
   */
  const formatWeekHeader = useCallback((weekStart: Date): string => {
    return `Uke ${getWeek(weekStart, { weekStartsOn: 1 })}`;
  }, []);

  // ============ VIEW-SPECIFIC CALCULATIONS ============

  /**
   * Get date range for a specific view mode
   */
  const getDateRangeForView = useCallback(
    (date: Date, mode: BookingViewMode): { start: Date; end: Date } => {
      switch (mode) {
        case "week":
          return {
            start: startOfWeek(date, { weekStartsOn: 1 }),
            end: endOfWeek(date, { weekStartsOn: 1 }),
          };
        case "month":
          return {
            start: startOfMonth(date),
            end: endOfMonth(date),
          };
        case "year":
          return {
            start: startOfYear(date),
            end: endOfYear(date),
          };
        default:
          return { start: date, end: date };
      }
    },
    [],
  );

  /**
   * Check if a date is within the current view
   */
  const isDateInView = useCallback(
    (date: Date): boolean => {
      return date >= dateStart && date <= dateEnd;
    },
    [dateStart, dateEnd],
  );

  /**
   * Get column count for grid display
   */
  const columnCount = useMemo((): number => {
    return visibleDays.length;
  }, [visibleDays]);

  /**
   * Get grid template columns CSS
   */
  const gridTemplateColumns = useMemo((): string => {
    // First column for row headers (employee/customer names)
    return `minmax(150px, auto) repeat(${columnCount}, minmax(80px, 1fr))`;
  }, [columnCount]);

  return {
    // State
    viewMode,
    setViewMode,
    hideWeekend,
    setHideWeekend,

    // View mode checks
    isWeekView,
    isMonthView,
    isYearView,

    // Date range
    dateStart,
    dateEnd,
    allDays,
    visibleDays,
    dayCount,
    visibleDayCount,

    // Day info
    dayInfos,

    // Week info
    weeks,
    weekCount,

    // Month info
    months,
    monthCount,

    // Labels
    dateRangeLabel,
    shortDateRangeLabel,

    // Formatting
    formatDate,
    formatDayHeader,
    formatWeekHeader,

    // View calculations
    getDateRangeForView,
    isDateInView,

    // Grid helpers
    columnCount,
    gridTemplateColumns,
  };
}
